<template>
  <div :class="$style.root">
    <div v-for="(product, index) in products" :key="product.document_id" :class="$style.cart_wrapper">
      <p :class="$style.index_title">{{ index + 1 }}つ目の商品</p>
      <div :class="$style.cart_container">
        <div :class="$style.carousel_wrapper">
          <PagePartsCartPartsCarousel
            :slide-images="slideImages[index]"
            :gift-wrapped="state.isConfirm && !!state.selectedGift.sku_id"
            :is-gift-wrapping-available-image="
              (path: string) =>
                state.displayedSkus[index]?.main_image !== path &&
                state.displayedSkus[index]?.sub_images.every((img) => img?.image_url !== path)
            "
            @expand-clicked="openSkuImageModal"
          />
        </div>

        <div :class="$style.detail">
          <div :class="[$style.line_container, $style.main]">
            <ProductLabel :product="product" />

            <div :class="$style.product_title">
              <h2>{{ product.name }}</h2>
              <p>{{ product.material }}</p>
            </div>

            <div :class="$style.price_container">
              <MoleculesConsumerCartPrice :sku="state.selectedSkus[index]"></MoleculesConsumerCartPrice>
            </div>
          </div>

          <div :class="[$style.line_container, $style.color_with_size]">
            <ClientOnly>
              <PagePartsCartPartsSelectColor
                v-if="product.skus && skusHasColor(product.skus)"
                :selected-sku="state.selectedSkus[index]"
                :cart-index="index"
                :skus="product.skus"
                :click-event="changeSelectedSku"
                :selected-color-label="selectedSkuColorLabel(index)"
                :color-type="product.color_type"
              />
            </ClientOnly>

            <div ref="sizeElementRef" :class="$style.size_container">
              <div :class="$style.label">
                <div :class="$style.left">
                  <p>{{ selectedSkuSizeLabel(index) }}</p>
                  <p>{{ selectedSkuSize(index) }}</p>
                </div>
                <AtomsConsumerLink
                  v-if="product.size_chart && (product.size_chart.pc_img_url || product.size_chart.sp_img_url)"
                  :class="$style.link"
                  @click="openSizeChartImageModal(product.size_chart.pc_img_url, product.size_chart.sp_img_url)"
                  >サイズチャートを見る</AtomsConsumerLink
                >
              </div>
              <p v-if="getRecommendSize(index)">おすすめサイズ：{{ getRecommendSize(index) }}</p>

              <PagePartsCartPartsSelectSize
                :cart-index="index"
                :selected-sku="state.selectedSkus[index]"
                :selected-size="state.selectedSize[index]"
                :skus="product.skus"
                :click-event="changeSelectedSku"
                :is-error="!!state.selectedSizeErrors[index]"
              />
            </div>
            <p v-if="!!state.selectedSizeErrors[index]" :class="[$style.error_message]">
              {{ state.selectedSizeErrors[index] }}
            </p>

            <p v-if="product.order_quantity_limit_type && product.order_quantity_limit">
              {{
                `※${
                  product.order_quantity_limit_type === "order"
                    ? "1回のご注文"
                    : "商品1点（ラッピング袋・カードを除く）"
                }ごとに、${product.order_quantity_limit}個までご購入可能です`
              }}
            </p>
            <p v-if="product.is_not_individual_order">
              {{ `※この商品のみのご注文は承っておりません。他の商品と一緒にご購入ください` }}
            </p>
            <AtomsConsumerButton
              v-if="product.show_recommend_size"
              variant="secondary"
              block
              @click="openSizeCheckModal(index)"
            >
              自分に合ったサイズを探す
            </AtomsConsumerButton>
          </div>
          <div v-if="isSkuOutOfStock(state.displayedSkus[index])" :class="$style.out_of_stock_label">
            <p>在庫切れ</p>
            <p v-if="outOfStockSkuShippingSupplement(index)">
              {{ outOfStockSkuShippingSupplement(index) }}
            </p>
            <p v-else-if="isNotArrival(index)">次回入荷予定はありません</p>
          </div>

          <div v-if="isShowReservationText(index)" :class="[$style.line_container]">
            <p :class="$style.tips">※この商品は予約注文となります</p>
          </div>
        </div>
      </div>
      <MoleculesConsumerModal v-if="displaySizeCheckIndex === index" v-model="displaySizeCheckRef">
        <PagePartsCartPartsSizeCheck
          :size-chart="state.sizeCharts[index]"
          :product-id="product.document_id"
          :product-name="product.name"
          :recommend-size="getRecommendSize(index)"
          :recommend-col-index="recommendColIndex"
          :recommend-row-index="recommendRowIndex"
          :is-foot-category="product.product_categories?.some((category) => category.slug === 'sandal')"
          :is-loading="isSizeCheckLoading"
          @check="(g, h, w, fs) => sizeCheckContentsOpen(g, index, h, w, fs)"
          @close="displaySizeCheckRef = false"
        />
      </MoleculesConsumerModal>
    </div>

    <PagePartsDuvetCollectionBanner :is-duvet-collection="!!products?.some((product) => product.is_duvet_collection)" />

    <div>
      <ProductNoveltyEndText :product="products ?? []" />
      <AtomsConsumerButton v-if="isEitherOutOfStock" variant="primary" disabled block :class="$style.bottom_button">
        在庫切れ
      </AtomsConsumerButton>
      <AtomsConsumerButton v-else variant="primary" block :class="$style.bottom_button" @click.prevent="openGiftModal">
        ラッピング設定へ進む
      </AtomsConsumerButton>
      <MoleculesConsumerFreeShippingText />
      <template v-if="eGiftConfigurable">
        <hr />
        <AtomsConsumerButton
          :variant="store.eGift.is_select ? 'primary_light' : 'secondary'"
          block
          rounded
          :class="$style.bottom_button"
          @click="openEGiftModal"
        >
          {{ store.eGift.is_select ? "eギフト：設定済み" : "eギフトで贈る" }}
        </AtomsConsumerButton>
        <p :class="$style.tips">
          ※eギフトの詳細は<AtomsConsumerLink @click="openEGiftExplanation">こちら</AtomsConsumerLink>
        </p>
      </template>
    </div>

    <MoleculesConsumerModal v-model="modalState.topImage.show">
      <div :class="$style.image_modal">
        <video
          v-if="isVideo(modalState.topImage.path)"
          autoplay
          muted
          loop
          playsinline
          :src="modalState.topImage.path"
        ></video>
        <AtomsConsumerImage v-else :src="modalState.topImage.path" />
      </div>
    </MoleculesConsumerModal>

    <MoleculesConsumerModal v-model="modalState.sizeChart.show">
      <div :class="$style.image_modal">
        <AtomsConsumerImage
          v-if="minScreenLg && modalState.sizeChart.pcImagePath"
          :src="modalState.sizeChart.pcImagePath"
          :width="1280"
        />
        <AtomsConsumerImage
          v-if="!minScreenLg && modalState.sizeChart.spImagePath"
          :src="modalState.sizeChart.spImagePath"
          :width="768"
        />
      </div>
    </MoleculesConsumerModal>

    <PagePartsCartPartsModal ref="siteCartModalRef">
      <PagePartsCartPartsSettingEGift
        v-model:message-value="store.eGift.message"
        v-model:name-value="store.eGift.giver_name"
        v-model:is-anonymous="store.eGift.isAnonymous"
        :sku-is-out-of-stock="skusIsOutOfStock"
        is-confirm
        is-hide-message
        :display-e-gift-ref="displayEGiftRef"
        @e-gift-confirm="eGiftConfirm"
        @e-gift-lift="eGiftLift"
      />
      <PagePartsCartPartsSettingGift
        v-show="displayGiftRef"
        :gift-wrappings="giftWrappings"
        :gift-messages="giftMessages"
        :gift-cards="giftCards"
        is-wrapping-recommend-cart
        @confirm="wrappingConfirm"
        @cancel="cancelGiftModal"
        @not-wrapping="notWrappingAddToCart"
      />
      <PagePartsCartPartsEGiftAnnotation
        :display-explanation-ref="displayEGiftExplanationRef"
        @e-gift-contents-close="eGiftContentsClose"
      />
    </PagePartsCartPartsModal>
    <OrderPreOrderAttention v-model:visible="isPreOrderModalVisible" @confirm="beforeVisibleCheckout" />
    <OrderDuvetCollectionModal
      v-model:visible="isDuvetCollectionModalVisible"
      @cancel="isDuvetCollectionModalVisible = false"
      @done="beforeVisibleCheckout"
    />
  </div>
</template>

<script setup lang="ts">
import { CartGift, CartItem } from "@tential/ec-gql-schema/models/order"
import { isEmbroideryProductsInCart } from "@tential/ec-gql-schema/utils/embroidery"
import { isVideo } from "@tential/ec-gql-schema/utils/functions/common"
import { PagePartsFragmentFragment } from "~/types/pageParts"
import { SELECT_GIFT_INIT_DATA } from "~/const/product"
import CartModal from "~/components/pageParts/cartParts/Modal.vue"
import { useStore } from "~/stores"
import { useToast } from "~/state"
import { cloneDeep } from "~/utils/functions/common"
import { EventHandler, AsyncEventHandler } from "~/types/events"
import { ProductFragmentFragment, SkuFragmentFragment } from "~/types/type.generated"
import { useCart } from "~/composables/useCart"
import {
  RecommendSizeChartGender,
  RecommendSizeChartResponse,
  RecommendSizeCharFootSize,
} from "~/types/server/api/recommend/size_chart"
import { useSizeCheck } from "~/composables/useSizeCheck"
import { useDuvetCollection } from "~/composables/useDuvetCollection"

type State = {
  selectedSkus: SkuFragmentFragment[] | []
  displayedSkus: SkuFragmentFragment[] | []
  selectedGift: CartGift
  isConfirm: boolean
  selectedSize: string[]
  selectedSizeErrors: string[]
  sizeCharts: RecommendSizeChartResponse[]
}

type ModalState = {
  topImage: {
    show: boolean
    path: string
  }
  sizeChart: {
    show: boolean
    pcImagePath: string
    spImagePath: string
  }
}
/**
 * MoleculesConsumerModal を利用したモーダルの表示切り替えと状態を保持する
 */
const modalState = ref<ModalState>({
  topImage: {
    show: false,
    path: "",
  },
  sizeChart: {
    show: false,
    pcImagePath: "",
    spImagePath: "",
  },
})

const { width: windowWidth } = useWindowSize()
// @mixin lg と同じ値に設定
const minScreenLg = computed(() => windowWidth.value >= 1024)

interface Props {
  products: PagePartsFragmentFragment["products"] | null
  giftPatternIds: string[] | null
  packageSize: number
}

const props = defineProps<Props>()

const state = reactive<State>({
  selectedSkus: [],
  displayedSkus: [],
  selectedGift: SELECT_GIFT_INIT_DATA,
  isConfirm: false,
  selectedSize: [],
  selectedSizeErrors: [],
  sizeCharts: [],
})

const store = useStore()
const cart = useCart()
const toast = useToast()
const {
  filterIndividualGiftWrappingsForGroupWrapping,
  filterIndividualMessagesForGroupWrapping,
  filterIndividualCardsForGroupWrapping,
  fetchGiftPattern,
} = useGift()
const { trackSizeRecommendClick } = useGtagEvent()
const { fetchRecommendSizeChart } = useSizeCheck()
const { showDuvetCollection, getDuvetCollectionType } = useDuvetCollection()
const { visitStore } = useStoreOrder()
const displayGiftRef = ref<boolean>(false)
const siteCartModalRef = ref<InstanceType<typeof CartModal>>()
const modalImageRef = ref<string>("")
const sizeElementRef = ref<HTMLElement[]>([])
const isPreOrderModalVisible = ref<boolean>(false)
const displayEGiftRef = ref<boolean>(false)
const displayEGiftExplanationRef = ref<boolean>(false)
const displaySizeCheckIndex = ref<number>(0)
const displaySizeCheckRef = ref<boolean>(false)
const isDuvetCollectionModalVisible = ref<boolean>(false)
const isSizeCheckLoading = ref<boolean>(false)

const giftWrappings = computed(() => {
  return filterIndividualGiftWrappingsForGroupWrapping(
    props.giftPatternIds || [],
    state.selectedGift.sku_id,
    props.packageSize,
  )
})

// ラッピング資材画像
const giftWrappingImageIndexes = computed(() => {
  return state.displayedSkus.map((sku) => {
    return sku.sub_images?.findIndex((img) => img?.is_wrapping) || -1
  })
})

const giftMessages = computed(() => {
  return filterIndividualMessagesForGroupWrapping(props.giftPatternIds || [], state.selectedGift.sku_id)
})

const giftCards = computed(() => {
  return filterIndividualCardsForGroupWrapping(props.giftPatternIds || [], state.selectedGift.sku_id)
})

const isIncludeReservation = computed(() => {
  return state.selectedSkus.some((sku) => sku.is_reservation)
})

// カートが空か
const isCartEmpty = computed(() => store.cartList.length === 0)

/**
 * eギフト設定可能
 */
const eGiftConfigurable = computed(() => {
  return (
    giftWrappings.value.length &&
    !state.selectedSkus.some((sku) => sku.is_reservation) &&
    allPricesAreEqual.value &&
    visitStore.value.isExpired &&
    !props.products?.some((product) => product.is_duvet_collection)
  )
})

const allPricesAreEqual = computed(() => {
  return props.products?.every((product) => {
    return cart.isAllSkuPricesEqual(product.skus)
  })
})

const skusIsOutOfStock = computed(() => {
  return props.products?.some((product) => {
    return product.skus?.some((sku) => isSkuOutOfStock(sku))
  })
})

const openEGiftModal = (): void => {
  if (isEmbroidery.value) {
    toast?.showErrorToasted("刺繍商品と通常商品は同時にカートに追加することができません")
    return
  }
  resetModalVisible()
  displayEGiftRef.value = true
  if (siteCartModalRef.value) siteCartModalRef.value.toggleModal()
}

const openEGiftExplanation = (): void => {
  resetModalVisible()
  displayEGiftExplanationRef.value = true
  if (siteCartModalRef.value) siteCartModalRef.value.toggleModal()
}

const eGiftContentsClose = (): void => {
  if (siteCartModalRef.value) siteCartModalRef.value.toggleModal()
}

const eGiftConfirm = (): void => {
  if (store.eGift.message === "" || store.eGift.giver_name === "") {
    toast?.showErrorToasted("メッセージ・贈り主を入力して下さい")
    return
  }
  if (!store.eGift.is_select && !isCartEmpty.value) {
    toast?.showErrorToasted("通常商品とは同時購入できません。eギフトではない商品をカートから削除してください")
    return
  }

  store.eGift.is_select = true
  store.setEGift(store.eGift)
  if (siteCartModalRef.value) siteCartModalRef.value.toggleModal()
  toast?.showSuccessToasted("eギフトの設定を完了しました")
  openGiftModal()
}

const eGiftLift = (): void => {
  store.resetEGift()
  if (siteCartModalRef.value) siteCartModalRef.value.toggleModal()
  toast?.showSuccessToasted("eギフトの設定を解除しました")
}

/**
 * サイズチャートの推奨サイズの行列のインデックス
 */
const recommendRowIndex = 1
const recommendColIndex = 1
const getRecommendSize = (index: number) => {
  if (!state.sizeCharts || !state.sizeCharts[index]) return ""

  const sizeChart = state.sizeCharts[index]
  const isFootCategory =
    props.products?.[index]?.product_categories?.some((category) => category.slug === "sandal") ?? false

  let size = ""

  if (isFootCategory) {
    size = sizeChart.footRecommendSizes?.[recommendColIndex] ?? ""
  } else {
    const row = sizeChart.apparelRecommendSizes?.[recommendRowIndex] ?? null
    size = row ? row[recommendColIndex] ?? "" : ""
  }

  return size && size !== "-" ? size : ""
}

const openSizeCheckModal = (index: number): void => {
  resetModalVisible()
  displaySizeCheckIndex.value = index
  displaySizeCheckRef.value = true
}

const isShowReservationText = (index: number): boolean => {
  return state.selectedSkus[index]?.is_reservation && !!state.selectedSize[index]
}

const initializeCartList = (): void => {
  if (props.products) {
    const selectedSkus: SkuFragmentFragment[] = []
    props.products.forEach((product) => {
      if (product.skus && product.skus.length > 0) {
        const inStockSku = product.skus.find((sku) => !isSkuOutOfStock(sku)) ?? product.skus[0]
        selectedSkus.push(inStockSku)
      }
    })
    state.selectedSkus = selectedSkus
    state.displayedSkus = selectedSkus
    state.selectedGift = SELECT_GIFT_INIT_DATA
    state.isConfirm = false
  }
}

initializeCartList()

const isEmbroidery = computed(() => isEmbroideryProductsInCart(store.cartList))

const isEitherOutOfStock = computed((): boolean => state.selectedSkus.some((sku) => isSkuOutOfStock(sku)))

const isOutOfStock = (index: number): boolean => {
  return isSkuOutOfStock(state.selectedSkus[index])
}

const slideImages = computed<string[][]>(() => {
  if (state.displayedSkus.length) {
    return state.displayedSkus.map((sku, i) => {
      const images = [sku.main_image ?? ""].filter(Boolean)
      sku.sub_images?.forEach((subImage) => subImage?.image_url && images.push(subImage.image_url))
      if (giftWrappingImageIndexes.value[i] < 0) {
        if (state.isConfirm && state.selectedGift.sku_id) {
          const giftSku = giftWrappings.value.find((sku) => sku.wrappingSku.document_id === state.selectedGift.sku_id)
          if (giftSku?.img) images.push(giftSku.img)
        } else {
          giftWrappings.value.forEach(
            (gift) => !gift.isOutOfStock && gift.wrappingSku.main_image && images.push(gift.wrappingSku.main_image),
          )
        }
      }
      return images
    })
  }
  return []
})

const selectedSkuSizeLabel = (index: number): string => state.selectedSkus[index].size_label || "サイズ"

const selectedSkuColorLabel = (index: number): string => state.selectedSkus[index].color_label || "カラー"

const selectedSkuSize = (index: number): string =>
  state.selectedSkus[index].size_supplement || state.selectedSize[index] || "未選択"

const resetModalVisible = (): void => {
  displayGiftRef.value = false
  displayEGiftRef.value = false
  displayEGiftExplanationRef.value = false
  displaySizeCheckRef.value = false
  modalImageRef.value = ""
}

const openGiftModal = async (): Promise<void> => {
  if (isEmbroidery.value) {
    toast?.showErrorToasted("刺繍商品と通常商品は同時にカートに追加することができません")
    return
  }

  if (!selectSizeValidation() && !store.eGift.is_select) {
    const index = state.selectedSize.findIndex((size) => size === "")
    sizeElementRef.value[index]?.scrollIntoView({ behavior: "smooth" })
    setSelectedSizeError()
    toast?.showErrorToasted("サイズを選択してください")
    return
  }
  resetModalVisible()
  await fetchGiftPattern()
  if (giftWrappings.value.length === 0) {
    addToCart()
    return
  }
  displayGiftRef.value = true
  if (siteCartModalRef.value) siteCartModalRef.value.toggleModal()
}

const cancelGiftModal = (): void => {
  state.isConfirm = false
  state.selectedGift = SELECT_GIFT_INIT_DATA
  if (siteCartModalRef.value) siteCartModalRef.value.toggleModal()
}

const wrappingConfirm = (tempSelectionGift: CartGift, isCard: boolean): void => {
  if (!isCard && !tempSelectionGift.sku_id) {
    toast?.showErrorToasted("ラッピングを選択して下さい")
    return
  } else if (isCard && !tempSelectionGift.card_sku_id) {
    toast?.showErrorToasted("カードを選択して下さい")
    return
  }

  state.isConfirm = true
  state.selectedGift = cloneDeep(tempSelectionGift)
  if (siteCartModalRef.value) siteCartModalRef.value.toggleModal()
  toast?.showSuccessToasted(`${isCard ? "メッセージカード" : "ギフトラッピング"}の設定を完了しました`)
  addToCart()
}

const openSizeChartImageModal: EventHandler = (pcImagePath: string | undefined, spImagePath: string | undefined) => {
  resetModalVisible()
  modalState.value.sizeChart.pcImagePath = pcImagePath ?? ""
  modalState.value.sizeChart.spImagePath = spImagePath ?? ""
  modalState.value.sizeChart.show = true
}

const openSkuImageModal = (path: string) => {
  resetModalVisible()
  modalState.value.topImage.path = path
  modalState.value.topImage.show = true
}

const isNotArrival = (index: number): boolean => {
  return !!state.selectedSkus[index] && isOutOfStock(index) && !state.selectedSkus[index].is_restock_notice
}

const outOfStockSkuShippingSupplement = (index: number) => {
  if (!state.selectedSkus[index]?.is_restock_notice)
    return state.selectedSkus[index]?.is_optional_shipping_supplement
      ? state.selectedSkus[index]?.shipping_supplement
      : "次回入荷予定はありません"
  return state.selectedSkus[index]?.shipping_supplement
}

const skusHasColor = computed((): ((_skus: SkuFragmentFragment[] | ProductFragmentFragment["skus"]) => boolean) => {
  return (_skus: SkuFragmentFragment[] | ProductFragmentFragment["skus"]): boolean => {
    return _skus.some((sku) => typeof sku.color === "string" && sku.color !== "")
  }
})

const changeSelectedSku: EventHandler = (_sku: SkuFragmentFragment, isColor: boolean, _cartIndex: number) => {
  state.selectedSizeErrors = []
  state.selectedSkus.splice(_cartIndex, 1, _sku)
  if (!isColor) state.selectedSize.splice(_cartIndex, 1, _sku.size)
  if (state.selectedSizeErrors[_cartIndex]) state.selectedSizeErrors.splice(_cartIndex, 1, "")
}

const selectSizeValidation = (): boolean => {
  // マルチカート内の全ての商品のサイズが選択されているかどうかを判定
  const productLength = props.products?.length ?? 0
  return state.selectedSize.length >= productLength && state.selectedSize.every((size) => size !== "")
}

const setSelectedSizeError = (): void => {
  // サイズ選択されていない商品がある場合、エラーメッセージを表示し、そのサイズ選択までスクロール
  state.selectedSizeErrors = []
  const productLength = props.products?.length ?? 0
  for (let i = 0; i < productLength; i++) {
    if (!state.selectedSize[i]) {
      state.selectedSizeErrors.splice(i, 1, "サイズを選択してください")
      const element = sizeElementRef.value[i]
      if (element)
        element.scrollIntoView({
          behavior: "smooth",
        })
      break
    }
    state.selectedSizeErrors.splice(i, 1, "")
  }
}

const notWrappingAddToCart = (): void => {
  if (siteCartModalRef.value) siteCartModalRef.value.toggleModal()
  addToCart()
}

const addToCart: AsyncEventHandler = async () => {
  if (isIncludeReservation.value) {
    isPreOrderModalVisible.value = true
  } else if (
    showDuvetCollection() &&
    props.products?.some((product) => product.is_duvet_collection) &&
    getDuvetCollectionType() === "none"
  ) {
    isDuvetCollectionModalVisible.value = true
  } else {
    await beforeVisibleCheckout()
  }
}

const addCartItems = async (): Promise<void> => {
  if (!props.products) {
    toast?.showErrorToasted("エラーが発生しました")
    return
  }
  const cartItems: CartItem[] = []
  for (let i = 0; i < props.products.length; i++) {
    if (
      (state.selectedGift.sku_id || state.selectedGift.card_sku_id) &&
      giftWrappings.value.every((gift) => gift.isOutOfStock)
    ) {
      state.selectedGift = SELECT_GIFT_INIT_DATA
    }
    cartItems.push({
      product_id: props.products[i].document_id,
      product_name: props.products[i].name,
      sku_id: state.selectedSkus[i].document_id,
      site_id: state.selectedSkus[i].site_id,
      sku_name: state.selectedSkus[i].name,
      is_reservation: state.selectedSkus[i].is_reservation,
      material: props.products[i].material,
      net_stock_quantity: state.selectedSkus[i].net_stock_quantity,
      net_stock_quantity_limit: state.selectedSkus[i].net_stock_quantity_limit,
      gift: undefined,
      is_duvet_collection: props.products[i].is_duvet_collection,
    })
  }
  // まとめてラッピング用ギフト資材
  if (state.selectedGift.sku_id) {
    const giftSku = giftWrappings.value.find((sku) => {
      return sku.wrappingSku.document_id === state.selectedGift.sku_id
    })
    if (giftSku && giftSku.product) {
      const groupWrappedUniqId = `${cartItems.map((item) => item.sku_id).join("&")}-${
        giftSku.wrappingSku.document_id
      }-${state.selectedGift.card_sku_id}-${state.selectedGift.message_sku_id}`
      for (let i = 0; i < cartItems.length; i++) {
        cartItems[i].group_wrapped_uniq_id = groupWrappedUniqId
      }
      cartItems.push({
        product_id: giftSku.product.document_id,
        product_name: giftSku.product.name,
        sku_id: giftSku.wrappingSku.document_id,
        site_id: giftSku.site_id,
        sku_name: giftSku.wrappingSku.name,
        is_reservation: giftSku.wrappingSku.is_reservation,
        material: giftSku.product.material,
        net_stock_quantity: giftSku.wrappingSku.net_stock_quantity,
        net_stock_quantity_limit: giftSku.wrappingSku.net_stock_quantity_limit,
        gift: undefined,
        group_wrapping: {
          wrapping_sku_ids: cartItems.map((item) => item.sku_id),
          gift: {
            sku_id: state.selectedGift.sku_id,
            sku_name: state.selectedGift.sku_name,
            sku_code: state.selectedGift.sku_code,
            product_id: state.selectedGift.product_id,
            product_name: state.selectedGift.product_name,
            img: state.selectedGift.img,
            price: state.selectedGift.price,
            size: state.selectedGift.size,
            message: state.selectedGift.message,
            message_img: state.selectedGift.message_img,
            message_price: state.selectedGift.message_price,
            message_sku_code: state.selectedGift.message_sku_code,
            message_sku_id: state.selectedGift.message_sku_id,
            card: state.selectedGift.card,
            card_img: state.selectedGift.card_img,
            card_price: state.selectedGift.card_price,
            card_sku_code: state.selectedGift.card_sku_code,
            card_sku_id: state.selectedGift.card_sku_id,
          },
        },
      })
    }
  }
  const skuIds = state.selectedSkus.map((sku) => sku.document_id)
  await cart.addCartMultiItem({ cartItems, skuIds })
}

const sizeCheckContentsOpen = async (
  gender: RecommendSizeChartGender,
  index: number,
  height?: number,
  weight?: number,
  foot_size?: RecommendSizeCharFootSize,
): Promise<void> => {
  try {
    isSizeCheckLoading.value = true
    const isFootCategory =
      props.products?.[index]?.product_categories?.some((category) => category.slug === "sandal") ?? false
    const { results, errorMessage } = await fetchRecommendSizeChart(
      props.products?.[index].document_id ?? "",
      gender,
      isFootCategory,
      height,
      weight,
      foot_size,
    )
    state.sizeCharts[index] = results
    if (errorMessage) {
      toast?.showErrorToasted(errorMessage)
      return
    }
    trackSizeRecommendClick({
      product_id: props.products?.[index].document_id ?? "",
      height,
      weight,
      gender,
      recommend_size: getRecommendSize(index),
    })
  } catch (e) {
    toast?.showErrorToasted("推奨サイズの取得に失敗しました")
  } finally {
    isSizeCheckLoading.value = false
  }
}

const beforeVisibleCheckout = async () => {
  await addCartItems()
  cart.openAddCartModal(state.selectedSkus.map((sku) => sku.document_id).concat(state.selectedGift.sku_id))
  isPreOrderModalVisible.value = false
  isDuvetCollectionModalVisible.value = false
  initializeCartList()
}

const setInitSelectSize = (): void => {
  if (props.products) {
    props.products.forEach((product, index) => {
      const inStockSkus = product.skus.filter((sku) => !isSkuOutOfStock(sku))
      const selectedSku = inStockSkus.length === 1 ? state.selectedSkus[index] : null
      state.selectedSize.push(selectedSku?.size || "")
    })
  }
}

watch(
  () => state.selectedSkus,
  () => {
    if (!props.products) return

    for (let index = 0; index < props.products.length; index++) {
      if (state.selectedGift.sku_id && state.isConfirm) {
        const isOutOfStock = giftWrappings.value.some(
          (gift) => gift.isOutOfStock && gift.wrappingSku?.product_id === state.selectedGift.product_id,
        )
        if (isOutOfStock) {
          state.isConfirm = false
          state.selectedGift = SELECT_GIFT_INIT_DATA
        }
      }
    }
  },
)

onMounted(() => {
  setInitSelectSize()
})
</script>

<style module lang="scss">
.root {
  @include full-container-lg;
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  background-color: $primary-10;
  gap: 2rem;
  padding: 2rem 0.5rem;
  @include lg {
    padding: 2rem 2rem;
  }
  .cart_wrapper {
    display: flex;
    flex-direction: column;
    gap: 1rem;
    background-color: $white;
    border-radius: 0.5rem;
    padding: 1.5rem 1rem;
    @include lg {
      padding: 1.5rem;
    }
    .index_title {
      @include main-font;
      font-size: $font-size-20;
      color: $primary;
      padding-left: 0.5rem;
    }
  }
  .cart_container {
    display: flex;
    flex-direction: column;
    gap: 2rem;

    @include lg {
      flex-direction: row;
    }

    .carousel_wrapper {
      display: flex;
      flex-direction: column;
      gap: 1rem;
      @include lg {
        width: 50%;
      }
    }

    .line_container {
      padding: 1.5rem 0;

      &:first-child {
        padding: 0 0 1.5rem;
      }
      &:last-child {
        padding: 1.5rem 0 0;
      }
      &:not(:last-child) {
        border-bottom: 1px solid $primary-20;
      }
    }

    .detail {
      @include lg {
        width: 50%;
      }

      .main {
        display: flex;
        flex-direction: column;
        gap: 0.5rem;

        .tag_list {
          display: flex;
          flex-wrap: wrap;
          align-items: flex-start;
          min-height: 1.375rem;
          margin-bottom: 0.5rem;
          gap: 0.25rem 0.5rem;
          .tag {
            font-size: $font-size-13;
            color: $primary;
            text-decoration: underline;
          }
        }

        .product_title {
          display: flex;
          flex-direction: column;
          gap: 0.25rem;
          h2 {
            font-size: $font-size-15;
            @include lg {
              font-size: $font-size-20;
            }
          }
          p {
            font-size: $font-size-12;
            @include lg {
              font-size: $font-size-14;
            }
          }
        }

        .price_container {
          .tax_in_price {
            display: flex;
            align-items: flex-end;
            gap: 0.75rem;
            .price {
              @include main-font;
              font-size: $font-size-20;
              .label {
                font-size: $font-size-10;
              }
              &.discount {
                color: $danger;
              }
            }

            .compare {
              @include main-font;
              display: flex;
              position: relative;
              align-items: flex-end;
              font-size: $font-size-15;
              .label {
                font-size: $font-size-9;
              }
              &::after {
                content: "";
                display: block;
                width: 100%;
                height: 1px;
                background-color: $black;
                position: absolute;
                top: 50%;
                left: 0;
              }
            }
          }
        }
      }

      .color_with_size {
        display: flex;
        flex-direction: column;
        gap: 1.125rem;

        .size_container {
          display: flex;
          flex-direction: column;
          gap: 0.875rem;

          .label {
            display: flex;
            justify-content: space-between;

            a,
            p {
              font-size: $font-size-12;
              @include lg {
                font-size: $font-size-14;
              }
            }

            .left {
              display: flex;
              gap: 0.5rem;
            }

            .link {
              cursor: pointer;
              text-decoration: underline;
            }
          }
        }
      }

      .delivery_date_container {
        display: flex;
        flex-direction: column;
        gap: 0.75rem;

        p {
          font-size: $font-size-12;
          @include lg {
            font-size: $font-size-14;
          }
        }

        .select_container {
          display: flex;
          gap: 0.875rem;
          align-items: center;

          .select {
            width: 100px;

            :global {
              select {
                font-size: $font-size-12;
                @include lg {
                  font-size: $font-size-14;
                }
              }
            }
          }
        }
      }

      .out_of_stock_label {
        margin-top: 1.25rem;
        font-size: $font-size-14;
      }
      .sold_out_label {
        @include sold-out-label-style;
      }
    }
  }

  .bottom_button {
    margin-bottom: 1rem;
  }

  .image_modal {
    @include lg {
      max-width: $max-width-md;
    }
    img,
    video {
      width: 100%;
      object-fit: contain;
    }
  }

  .tips {
    font-size: $font-size-10;
    @include lg {
      font-size: $font-size-12;
    }
    a {
      text-decoration: underline;
      cursor: pointer;
    }
  }

  .error_message {
    margin-top: 0.5rem;
    font-size: 12px;
    font-weight: bold;
    color: $danger;
  }
}
</style>
